<template>
  <div>
    <v-data-table
      v-model="computedSelectedItems"
      class="checklist-global-attached-files-dialog-table"
      :items="items"
      :mobile="false"
      :headers="headers"
      :footer-props="{
        'items-per-page-options': ITEM_PER_PAGE_DEFAULT,
        showCurrentPage: true,
      }"
      show-select
      return-object
      :row-props="rowProps"
    >
    <template v-slot:header.data-table-select="{ allSelected, selectAll, someSelected }">
      <v-checkbox-btn
        :indeterminate="someSelected && !allSelected"
        :model-value="allSelected"
        @update:model-value="selectAll(!allSelected)"
      ></v-checkbox-btn>
    </template>
    <template v-slot:item.data-table-select="{ internalItem, isSelected, toggleSelect }">
      <v-checkbox-btn
        :model-value="isSelected(internalItem)"
        @update:model-value="toggleSelect(internalItem)"
      ></v-checkbox-btn>
    </template>
      <template #item.basename="{item}">
        <div class="d-flex align-center column-gap-1">
          <ClosdFildersIcon :document="item" />
            <span
              class="filderName clickable"
              data-locator="file-name"
              @click="onOpenDocument(item)"
            >
              <TruncatedText>{{ item.basename }}</TruncatedText>
            </span>
          <app-badge v-if="item.version > 1">{{ `v${item.version}` }}</app-badge>
        </div>
      </template>
      <template #item.parent="{item}">
        <TruncatedText>{{ item?.parent?.name }}</TruncatedText>
      </template>
      <template #item.rights="{item}">
        <div v-if="isCurrentUserPm" style="max-width: 120px">
          <RightsDisplayButton :rights="item.rights" @click="onShareButtonClick(item)" />
        </div>
      </template>
      <template #item.size="{item}">
        <app-filesize-display :value="item.size" />
      </template>
      <template #item.updatedBy="{item}">
        <span>{{ item.updateUser }}</span>
      </template>
      <template #item.updatedAt="{item}">
        <span>{{ fileDate(item.updateTime) }}</span>
      </template>
      <template #item.actions="{item}">
        <div class="d-flex align-center justify-end column-gap-2">
          <OfficeOnlineEditionButton v-if="isOfficeEditionButtonShowed(item)" :document="item"/>
          <AppDownloadFile :link="fileLink(item.id)">
            <template #default="{ submitForm }">
              <app-button type="icon"
                          @click="submitForm"
              >
                <app-material-icon name="download" color="inherit" />
              </app-button>
            </template>
          </AppDownloadFile>
          <ChecklistTableTaskAttachedFilesItemMenu :mnemo="mnemo" :file="item" globalDialog/>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import axios from 'axios'
import { cloneDeep } from 'lodash-es'
import { defineComponent, PropType } from 'vue'
import { mapActions, mapGetters } from 'vuex'

import AppDownloadFile from '@/common/AppDownloadFile.vue'
import TruncatedText from '@/common/TruncatedText.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import OfficeOnlineEditionButton from '@/common/office/OfficeOnlineEditionButton.vue'
import RightsDisplayButton from '@/common/users/RightsDisplayButton.vue'
import { ISOToShortenedDate } from '@/common/utils/dates'
import { openFileViewer } from '@/common/utils/files'
import ChecklistTableTaskAttachedFilesItemMenu from '@/project/checklist/table/ChecklistTableTaskAttachedFilesItemMenu.vue'
import { ChecklistAttachedFileType } from '@/project/checklist/types'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { SET_TABLE_DOCUMENTS_PANE } from '@/store/modules/documents/action_types'


const ITEM_PER_PAGE_DEFAULT = [10, 25, 50]

export default defineComponent({
  components: {
    AppDownloadFile,
    ChecklistTableTaskAttachedFilesItemMenu,
    ClosdFildersIcon,
    OfficeOnlineEditionButton,
    RightsDisplayButton,
    TruncatedText,
  },
    name: 'ChecklistGlobalAttachedFilesDialogTable',
    props: {
      items: {
        type: Array as PropType<ChecklistAttachedFileType[]>,
        required: true,
      },
      // Selected items model
      value: {
        type: Array,
        default: () => [],
      },
      mnemo: {
        type: String,
        default: '',
      },
      selectedItems: {
        type: Array as PropType<ChecklistAttachedFileType[]>,
        required: true,
      },
    },
    data () {
      return {
        headers: [
          {
            value: 'basename',
            title: this.$t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.ChecklistGlobalAttachedFilesDialogTable.headers.filename'),
          },
          {
            value: 'parent',
            title: this.$t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.ChecklistGlobalAttachedFilesDialogTable.headers.taskParent'),
            sort (a: { name?: string }, b: { name?: string }) {
              return (a.name || '').localeCompare(b.name || '')
            },
          },
          {
            value: 'rights',
            title: this.$t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.ChecklistGlobalAttachedFilesDialogTable.headers.rights'),
            sortable: false,
          },
          {
            value: 'size',
            title: this.$t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.ChecklistGlobalAttachedFilesDialogTable.headers.size'),
          },
          {
            value: 'updatedBy',
            title: this.$t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.ChecklistGlobalAttachedFilesDialogTable.headers.updatedBy'),
          },
          {
            value: 'updatedAt',
            title: this.$t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.ChecklistGlobalAttachedFilesDialogTable.headers.updatedAt'),
          },
          {
            value: 'actions',
            title: this.$t('project.checklist.dialogs.ChecklistGlobalAttachedFilesDialog.ChecklistGlobalAttachedFilesDialogTable.headers.actions'),
            sortable: false,
          },
        ],
        ITEM_PER_PAGE_DEFAULT,
      }
    },
    computed: {
      ...mapGetters('room', ['isCurrentUserPm', 'isWopiEnabled']),
      computedSelectedItems: {
          get (): Array<unknown> {
            return this.selectedItems
          },
          set (newValue: Array<unknown>) {
            this.$emit('setSelectedItems', newValue)
          },
      },
    },
    methods: {
      ...mapActions('documentSharePicker', [
        DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
        DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
      ]),
      ...mapActions('documents', [SET_TABLE_DOCUMENTS_PANE]),
      onShareButtonClick (item: ChecklistAttachedFileType) {
        this.INIT_SELECTED_GROUPS(cloneDeep(item.rights.read.groups))
        this.INIT_SELECTED_MEMBERS(cloneDeep(item.rights.read.users))
        this.SET_TABLE_DOCUMENTS_PANE({
          isOpen: true,
          tab: 'share',
          documents: [item],
          checklistFilesParent: item?.parent,
        })
      },
      fileDate (updateDate: string) {
        return ISOToShortenedDate(updateDate)
      },
      fileLink (fileId: number) {
        return `${axios.defaults.baseURL}/room/${this.mnemo}/downloads/file/${fileId}`
      },
      isOfficeEditionButtonShowed (document: ChecklistAttachedFileType) {
        return this.isWopiEnabled && document.canEdit && this.isCurrentUserPm
      },
      onOpenDocument (file: ChecklistAttachedFileType) {
        openFileViewer(this.mnemo, file.id)
      },
      rowProps(row: any) {
        const item = row.item
        return {
          class: this.computedSelectedItems.some(selected => (selected as ChecklistAttachedFileType).id === item.id) ? 'selected' : '',
        }
      },
    },
  })
</script>

<style scoped lang="scss">
.filderName {
  max-width: 450px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

:deep(.v-data-table__tr) {
  cursor: default;

  &:hover {
    background-color: rgb(var(--v-theme-surface-hover));
  }
  &.selected {
     background-color: rgb(var(--v-theme-surface-info)) !important;
  }
}
</style>
